<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalTransactionType v-if="modalShowTransactionType" @closeModal="closeModal"
    :transactionType="selectedTransactionType" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Tipe Transaksi" />
      <div class="bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton to_url="#" button_text="+ Tambah Tipe" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Nama</TableHeadCell>
                    <TableHeadCell>Perlu Meja</TableHeadCell>
                    <TableHeadCell>Tutup Bill</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allTransactionTypes.length == 0">
                    <TableCell colspan="6" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(type, index) in allTransactionTypes" :key="type.id" :index="index" isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ type.name }}</TableCell>
                    <TableCell>
                      <span :class="type.need_table
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        " class="px-2 py-1 rounded-full text-xs font-medium">
                        {{ type.need_table ? "Ya" : "Tidak" }}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span :class="type.close_bill
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        " class="px-2 py-1 rounded-full text-xs font-medium">
                        {{ type.close_bill ? "Ya" : "Tidak" }}
                      </span>
                    </TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + type.id" @click="editTransactionType(type)"
                        class="border border-gray-400 hover:border-green-700 hover:bg-green-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 group-hover:text-green-700" />
                      </button>
                      <button :id="'delete-data-' + type.id" @click="deleteTransactionType(type)"
                        class="border border-gray-400 hover:border-red-500 hover:bg-red-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <TrashIcon class="h-4 w-4 group-hover:text-red-700" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ModalTransactionType from "@/components/modals/ModalTransactionType.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/solid";
import { mapGetters, mapActions } from "vuex";
import ViewContainer from "@/components/ViewContainer.vue";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "TransactionTypeView",
  components: {
    IndexView, PageTitle, AddButton, ModalTransactionType, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, PaginationButtons, ConfirmDialog, Toast, Button
  },
  data() {
    return {
      modalShowTransactionType: false,
      isBlur: false,
      selectedTransactionType: null,
      currentPage: 1,
      totalPages: 1,
    };
  },
  computed: {
    ...mapGetters("transaction_type", ["allTransactionTypes"]),
  },
  created() {
    this.$store.dispatch("transaction_type/fetchTransactionTypes");
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchTransactionTypes({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions("transaction_type", ["deleteTransactionTypeById"]),
    async fetchTransactionTypes(params = {}) {
      if (!params.page) params.page = this.currentPage;
      const { pagination } = await this.$store.dispatch("transaction_type/fetchTransactionTypes", params);
      if (pagination) {
        this.totalPages = pagination.totalPages;
      }
    },
    showModal() {
      this.modalShowTransactionType = true;
      this.isBlur = true;
      this.selectedTransactionType = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowTransactionType = false;
        this.isBlur = false;
        this.fetchTransactionTypes();
      }, 200);
    },
    editTransactionType(type) {
      this.selectedTransactionType = type;
      this.modalShowTransactionType = true;
      this.isBlur = true;
    },
    async deleteTransactionType(type) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus tipe transaksi?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteTransactionTypeById(type.id);
            this.fetchTransactionTypes();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus tipe transaksi', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus tipe transaksi', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus tipe transaksi', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus tipe transaksi', life: 3000 });
        }
      });
    },
  },
};
</script>
